import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { BillingPlan } from "../api";
import { useI18n, useT, type TranslationKey } from "../i18n";
import { localeCurrency, pickPrice } from "../money";
import {
  cardPricing,
  formatPaywallMoney,
  monthsForCard,
  type CardKey,
} from "../paywallPricing";
import "./Paywall.css";

/** Presentation only. Every figure — rate, period total, savings, discount —
 *  comes from the backend plan paired with this card; see paywallPricing.ts. */
type CardDef = {
  key: CardKey;
  featured?: boolean;
  pill?: { label: TranslationKey; tone: "green" | "purple" };
  discountTone?: "green" | "red";
  title: TranslationKey;
  /** Takes the period total, e.g. "{amount} per quarter". */
  billed: TranslationKey;
  saveTone?: "green" | "yellow";
  tagline: TranslationKey;
  lead?: TranslationKey;
  features: TranslationKey[];
  cta: TranslationKey;
};

const CARDS: CardDef[] = [
  {
    key: "1m",
    title: "paywall.m1Title",
    billed: "paywall.m1Billed",
    tagline: "paywall.m1Tagline",
    features: [
      "paywall.m1FeatUnlimited",
      "paywall.m1FeatDepth",
      "paywall.m1FeatRetests",
      "paywall.m1FeatStart",
      "paywall.m1FeatReports",
      "paywall.m1FeatIntegrations",
      "paywall.m1FeatSupport",
    ],
    cta: "paywall.m1Cta",
  },
  {
    key: "3m",
    pill: { label: "paywall.mostPopular", tone: "green" },
    discountTone: "green",
    title: "paywall.m3Title",
    saveTone: "green",
    billed: "paywall.m3Billed",
    tagline: "paywall.m3Tagline",
    lead: "paywall.m3Lead",
    features: [
      "paywall.m3FeatDepth",
      "paywall.m3FeatQueue",
      "paywall.m3FeatAutoPatch",
      "paywall.m3FeatChains",
      "paywall.m3FeatSupport",
      "paywall.m3FeatWhiteLabel",
      "paywall.m3FeatJira",
      "paywall.m3FeatContract",
    ],
    cta: "paywall.m3Cta",
  },
  {
    key: "12m",
    featured: true,
    pill: { label: "paywall.bestValue", tone: "purple" },
    discountTone: "red",
    title: "paywall.m12Title",
    saveTone: "yellow",
    billed: "paywall.m12Billed",
    tagline: "paywall.m12Tagline",
    lead: "paywall.m12Lead",
    features: [
      "paywall.m12FeatDepth",
      "paywall.m12FeatContinuous",
      "paywall.m12FeatVip",
      "paywall.m12FeatRemediation",
      "paywall.m12FeatPaths",
      "paywall.m12FeatTam",
      "paywall.m12FeatCompliance",
      "paywall.m12FeatBoardReport",
      "paywall.m12FeatIntegrations",
      "paywall.m12FeatPriceLock",
      "paywall.m12FeatSla",
    ],
    cta: "paywall.m12Cta",
  },
];

/** Fullscreen subscription paywall (Figma node 2031:998). The marketing copy
 * and feature lists are static; each card is paired positionally with a backend
 * plan (cards and `plans` are both ordered shortest period first) so its button
 * checks that plan out directly. A card with no matching plan hands back null
 * and the caller falls back to the generic plan picker. */
export function PaywallOverlay({
  plans,
  currentTier,
  busyTier,
  error,
  onSelect,
  onClose,
}: {
  plans: BillingPlan[] | null;
  currentTier: string | null;
  busyTier: string | null;
  error: string | null;
  onSelect: (plan: BillingPlan | null) => void;
  onClose: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const priceOf = (plan: BillingPlan | null) =>
    plan ? pickPrice(plan.prices, localeCurrency(lang)) : null;
  // The shortest plan sets the baseline the longer ones are discounted against.
  const basePrice = priceOf(plans?.[0] ?? null);
  const baseMonthly = basePrice ? basePrice.amount / monthsForCard(CARDS[0].key) : null;

  // Portal to <body>: the page's .anim-page transform forms the containing
  // block for `position: fixed`, which would offset this overlay from the
  // viewport (same fix as ConfirmDialog).
  return createPortal(
    <div
      className="paywall"
      role="dialog"
      aria-modal="true"
      aria-label={t("paywall.title")}
      onMouseDown={onClose}
    >
      <button
        type="button"
        className="paywall__close"
        aria-label={t("common.close")}
        onClick={onClose}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* The design draws the cross as a plus rotated 45°. */}
        <svg width={24} height={24} viewBox="0 0 18 18" fill="none" style={{ transform: "rotate(45deg)" }}>
          <path d="M9 1V17M17 9L1 9" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
        </svg>
      </button>
      <header className="paywall__head" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="paywall__title">{t("paywall.title")}</h2>
        <p className="paywall__subtitle">{t("paywall.subtitle")}</p>
      </header>
      <div className="paywall__cards">
        {CARDS.map((c, i) => {
          const plan = plans?.[i] ?? null;
          const isCurrent = plan != null && plan.plan_tier === currentTier;
          const isBusy = plan != null && plan.plan_tier === busyTier;
          const price = priceOf(plan);
          const pricing = price
            ? cardPricing(price.amount, monthsForCard(c.key), baseMonthly)
            : null;
          const currency = price?.currency ?? "";
          const money = (v: number) => formatPaywallMoney(v, currency, lang);
          return (
          <article
            key={c.key}
            className={`paywall__card${c.featured ? " paywall__card--featured" : ""}${
              isCurrent ? " paywall__card--current" : ""
            }`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {c.pill ? (
              <span className={`paywall__pill paywall__pill--${c.pill.tone}`}>
                {t(c.pill.label, {
                  months: pricing ? Math.round(pricing.freeMonths * 10) / 10 : 0,
                })}
              </span>
            ) : null}
            {c.discountTone && pricing && pricing.discountPct > 0 ? (
              <span className={`paywall__discount paywall__discount--${c.discountTone}`}>
                −{pricing.discountPct}%
              </span>
            ) : null}
            <div className="paywall__card-top">
              <div className="paywall__card-lede">
                <h3 className="paywall__card-title">{t(c.title)}</h3>
                <div className="paywall__price-block">
                  <p className="paywall__price">
                    <span className="paywall__price-amount">
                      {pricing ? money(pricing.perMonth) : "—"}
                    </span>{" "}
                    <span className="paywall__price-unit">{t("paywall.perAsset")}</span>
                  </p>
                  <p className="paywall__billed">
                    {c.saveTone && pricing && pricing.savings > 0 ? (
                      <span className={`paywall__save paywall__save--${c.saveTone}`}>
                        {t("paywall.save", { amount: money(pricing.savings) })}
                      </span>
                    ) : null}
                    {pricing ? t(c.billed, { amount: money(pricing.total) }) : ""}
                  </p>
                </div>
                <p className="paywall__tagline">{t(c.tagline)}</p>
                <hr className="paywall__divider" />
              </div>
              <ul className="paywall__features">
                {c.lead ? (
                  <li className="paywall__feature paywall__feature--lead">{t(c.lead)}</li>
                ) : null}
                {c.features.map((f) => (
                  <li key={f} className="paywall__feature">
                    {t(f)}
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              className="paywall__cta"
              disabled={isCurrent || busyTier !== null}
              onClick={() => onSelect(plan)}
            >
              {isBusy ? (
                <>
                  <span className="paywall__spinner" aria-hidden="true" />
                  {t("settings.choosingPlan")}
                </>
              ) : isCurrent ? (
                t("settings.currentPlanBadge")
              ) : (
                t(c.cta)
              )}
            </button>
          </article>
          );
        })}
      </div>
      {error ? (
        <p className="paywall__error" role="alert" onMouseDown={(e) => e.stopPropagation()}>
          {error}
        </p>
      ) : null}
      {/* Stripe adds tax on top of the catalog price — warn before checkout,
          not on the invoice. */}
      <p className="paywall__tax-note" onMouseDown={(e) => e.stopPropagation()}>
        {t("settings.taxNote")}
      </p>
    </div>,
    document.body,
  );
}
